import { useMemo, useState } from "preact/hooks";

import { AnimalCard } from "../components/AnimalCard";
import { AnimalFormSheet } from "../components/AnimalFormSheet";
import { useAnimalHealthNotes } from "../hooks/useAnimalHealthNotes";
import type { useAnimals } from "../hooks/useAnimals";
import { attachmentThumbUrl } from "../lib/api";
import {
  ADD_ANIMAL_BUTTON_LABEL,
  animalArchivedMessage,
  animalRestoredMessage,
  animalsListCountAnnouncement,
  ANIMALS_EMPTY_HINT,
  ANIMALS_FILTER_ACTIVE_LABEL,
  ANIMALS_FILTER_ALL_LABEL,
  ANIMALS_FILTER_TOGGLE_LABEL,
  ANIMALS_SHOW_ALL_LABEL,
  noActiveAnimalsHint,
  speciesDatalistOptions,
} from "../lib/recordsCopy";
import { AnimalDetailOverlay } from "./AnimalDetailOverlay";

export interface AnimalsViewProps {
  animals: ReturnType<typeof useAnimals>;
  isAdmin: boolean;
  onNotify: (message: string) => void;
}

type AnimalsFilter = "active" | "all";

/**
 * Sprint-005 C4 — the More tab's Animals screen: warm avatar-row cards (name
 * + species), tap through to the detail/timeline overlay. Every authed role
 * reaches this screen (`GET /animals` has no role restriction server-side)
 * — see `AnimalDetailOverlay` for the admin-only affordances inside it.
 *
 * Sprint-011 A8 — badges a recent-health-note animal exactly the way
 * Dashboard's Animals-snapshot mini-grid already does (`.has-health-note`/
 * "Health note" pill) — same `useAnimalHealthNotes` fan-out, same 14-day
 * window, shared with Dashboard.
 *
 * Sprint-012 — card visuals moved to `../components/AnimalCard` (design-
 * spec-animal-cards-2026-07-10.md §2, Option B: 56px species-silhouette
 * avatar + status-chip row), consolidating what used to be two copy-pasted
 * `AnimalCard` implementations here and in `DashboardView.tsx` into one
 * shared component (UX §1.1's consolidation ask).
 *
 * Sprint-013 C1 (design-spec-animal-cards-2026-07-10.md §5, ux-ruling-
 * inactive-grid-2026-07-10.md — E1 PM ruling, BINDING) — resolves TD-023:
 * this screen now wires `AnimalCard`'s `inactive` prop to real
 * `animal.active === false` data, gated behind a two-state "Active"
 * (default) / "All" toggle in the header, beside the new admin-only
 * "+ Add" entry point. No toggle state is persisted across mounts —
 * every fresh visit starts on "Active" (E1 ruling item 1). Also adds the
 * Add/Edit/Archive CRUD flow (design-spec §5) — Edit and Archive live
 * inside `AnimalDetailOverlay`, admin-only.
 */
export function AnimalsView({ animals, isAdmin, onNotify }: AnimalsViewProps) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [filter, setFilter] = useState<AnimalsFilter>("active");
  const [showAdd, setShowAdd] = useState(false);
  const openAnimal = animals.animals?.find((a) => a.id === openId) ?? null;

  const allAnimals = animals.animals ?? [];
  const activeAnimals = useMemo(() => allAnimals.filter((a) => a.active), [allAnimals]);
  const inactiveCount = allAnimals.length - activeAnimals.length;
  const visibleAnimals = filter === "all" ? allAnimals : activeAnimals;
  const speciesOptions = useMemo(() => speciesDatalistOptions(allAnimals), [allAnimals]);

  const animalIds = useMemo(() => (animals.animals ?? []).map((a) => a.id), [animals.animals]);
  const { loading: eventsLoading, recentNoteLevel } = useAnimalHealthNotes(animalIds);

  return (
    <div>
      <div class="animals-view-toolbar">
        <div class="animals-filter-toggle" role="group" aria-label={ANIMALS_FILTER_TOGGLE_LABEL}>
          <button
            type="button"
            class={`animals-filter-btn${filter === "active" ? " is-selected" : ""}`}
            aria-pressed={filter === "active"}
            onClick={() => setFilter("active")}
          >
            {ANIMALS_FILTER_ACTIVE_LABEL}
          </button>
          <button
            type="button"
            class={`animals-filter-btn${filter === "all" ? " is-selected" : ""}`}
            aria-pressed={filter === "all"}
            onClick={() => setFilter("all")}
          >
            {ANIMALS_FILTER_ALL_LABEL}
          </button>
        </div>
        {isAdmin ? (
          <button
            type="button"
            class="primary-btn animals-add-btn"
            onClick={() => setShowAdd(true)}
          >
            {ADD_ANIMAL_BUTTON_LABEL}
          </button>
        ) : null}
      </div>

      {/* ux-ruling-inactive-grid-2026-07-10.md — "List count announcement"
       * a11y requirement: a live region so the announced count refreshes
       * when the toggle changes, independent of native list-focus timing. */}
      <p class="sr-only" aria-live="polite">
        {animals.loading || animals.error
          ? ""
          : animalsListCountAnnouncement(visibleAnimals.length)}
      </p>

      {animals.loading ? (
        <p class="loading-hint">Loading animals…</p>
      ) : animals.error ? (
        <p class="error-hint" role="alert">
          {animals.error}
        </p>
      ) : allAnimals.length === 0 ? (
        <p class="empty-hint">{ANIMALS_EMPTY_HINT}</p>
      ) : visibleAnimals.length === 0 ? (
        <p class="empty-hint">
          {noActiveAnimalsHint(inactiveCount)}{" "}
          <button type="button" class="inline-link-btn" onClick={() => setFilter("all")}>
            {ANIMALS_SHOW_ALL_LABEL}
          </button>
        </p>
      ) : (
        <ul class="animal-grid">
          {visibleAnimals.map((animal) => (
            <li key={animal.id}>
              <AnimalCard
                animal={animal}
                hasHealthNote={!eventsLoading && recentNoteLevel(animal.id) === "attention"}
                hasRoutineCare={!eventsLoading && recentNoteLevel(animal.id) === "routine"}
                onOpen={() => setOpenId(animal.id)}
                photoUrl={
                  animal.primary_photo_attachment_id
                    ? attachmentThumbUrl(animal.primary_photo_attachment_id)
                    : undefined
                }
                inactive={!animal.active}
              />
            </li>
          ))}
        </ul>
      )}

      {isAdmin ? (
        <AnimalFormSheet
          open={showAdd}
          mode="add"
          speciesOptions={speciesOptions}
          onCancel={() => setShowAdd(false)}
          onAdd={async (fields) => {
            const created = await animals.createAnimal(fields);
            setShowAdd(false);
            onNotify(`Added ${created.name}.`);
          }}
        />
      ) : null}

      {openAnimal ? (
        <AnimalDetailOverlay
          animal={openAnimal}
          isAdmin={isAdmin}
          speciesOptions={speciesOptions}
          onClose={() => setOpenId(null)}
          onNotify={onNotify}
          onSetPrimaryPhoto={(attachmentId) => animals.setPrimaryPhoto(openAnimal.id, attachmentId)}
          onSaveFields={async (fields) => {
            await animals.updateAnimalFields(openAnimal.id, fields);
          }}
          onArchive={async () => {
            await animals.archiveAnimal(openAnimal.id);
            setOpenId(null);
            onNotify(animalArchivedMessage(openAnimal.name));
          }}
          onRestore={async () => {
            await animals.restoreAnimal(openAnimal.id);
            setOpenId(null);
            onNotify(animalRestoredMessage(openAnimal.name));
          }}
        />
      ) : null}
    </div>
  );
}
